import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { ActiveToast, IndividualConfig, ToastContainerDirective, ToastrService } from "ngx-toastr";
import { BehaviorSubject, Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { AlertItem } from "./alert.module";

export const INLINE_TOASTR_CONFIG: Partial<IndividualConfig> = {
  positionClass: "inline",
  enableHtml: true,
  tapToDismiss: true,
  closeButton: true,
  disableTimeOut: true
};

@Component({
  selector: "vp-alert-container",
  standalone: false,
  template: "<span toastContainer></span>",
  styleUrls: ["./alert.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ToastrService]
})
export class AlertComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() alerts: AlertItem[] = [];
  @Input() config: Partial<IndividualConfig> = INLINE_TOASTR_CONFIG;
  @Output() alertDismissed = new EventEmitter<AlertItem>();

  @ViewChild(ToastContainerDirective, { static: true })
  toastContainer!: ToastContainerDirective;

  private readonly alertItems$$ = new BehaviorSubject<AlertItem[]>([]);
  private readonly destroyed$$ = new Subject<void>();

  constructor(private toastrService: ToastrService) {}

  private activeToasts: ActiveToast<any>[] = [];

  ngOnInit(): void {
    this.toastrService.toastrConfig.preventDuplicates = false;
  }

  ngAfterViewInit(): void {
    this.toastrService.overlayContainer = this.toastContainer;

    this.alertItems$$.pipe(takeUntil(this.destroyed$$)).subscribe(alerts => {
      this.removeOldAlerts(alerts);
      alerts.forEach((alert: AlertItem) => {
        this.showAlert(alert);
      });
    });
  }

  private removeOldAlerts(newAlerts: AlertItem[]) {
    this.activeToasts.forEach(toast => {
      const stillActive = newAlerts.some(
        alert => alert.message === toast.message && alert.title === toast.title
      );
      if (!stillActive) {
        this.toastrService.remove(toast.toastId);
      }
    });
  }

  private showAlert(alert: AlertItem) {
    if (!alert.message || this.isAlreadyShown(alert)) return;

    const activeToast = this.toastrService.show(
      alert.message,
      alert.title,
      this.config,
      alert.type || "toast-info"
    );

    this.activeToasts.push(activeToast);

    activeToast.onHidden.pipe(take(1)).subscribe(() => {
      this.alertDismissed.emit(alert);
      this.activeToasts = this.activeToasts.filter(toast => toast !== activeToast);
    });
  }

  private isAlreadyShown(alert: AlertItem) {
    return this.activeToasts.some(toast => toast.message === alert.message);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.alertItems$$.next(changes.alerts.currentValue ?? []);
  }

  ngOnDestroy(): void {
    this.toastrService.clear();
    this.destroyed$$.next();
    this.destroyed$$.complete();
  }
}
